import config from '../../config';
import { getAuthHeaders, handleNoContentResponse, handleResponse } from './common';

const buildQueryString = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

const withJsonBody = (method, body) => ({
  method,
  headers: getAuthHeaders(),
  body: JSON.stringify(body ?? {})
});

const buildVoidFormRequest = (request = {}) => {
  const { reason, voidReason, VoidReason, ...rest } = request;
  const resolvedVoidReason = VoidReason ?? voidReason ?? reason;
  return resolvedVoidReason === undefined
    ? request
    : { ...rest, VoidReason: resolvedVoidReason };
};

const getFilenameFromDisposition = (disposition, fallback) => {
  const match = String(disposition || '').match(/filename\*?=(?:UTF-8''|["']?)([^"';\n]+)/i);
  return match ? decodeURIComponent(match[1].replace(/^["']|["']$/g, '')) : fallback;
};

const downloadPdf = async (url, fallbackFilename) => {
  const response = await fetch(url, { headers: getAuthHeaders() });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail || `Download failed: ${response.status}`);
  }
  const blob = await response.blob();
  return {
    blob,
    filename: getFilenameFromDisposition(response.headers.get('Content-Disposition'), fallbackFilename),
  };
};

const formsApi = {
  createFormRevision: async (referenceId) => {
    const response = await fetch(`${config.api.url}/forms/${referenceId}/revisions`, withJsonBody('POST', { copyFieldValues: true, useLatestTemplateVersion: false }));
    return handleResponse(response);
  },
  questionnaireAction: async (referenceId, action, request, portal = false) => {
    const response = await fetch(`${config.api.url}/${portal ? 'client-portal/forms' : 'forms'}/${referenceId}/questionnaire-draft/${action}`, withJsonBody('POST', request));
    if (response.status === 409) throw Object.assign(new Error('This form changed. Reload before continuing.'), { code: 'questionnaireDraftConflict' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Unable to complete this action.');
    }
    return response.json();
  },
  getQuestionnaireAnswers: async (referenceId, portal = false) => {
    const response = await fetch(`${config.api.url}/${portal ? 'client-portal/forms' : 'forms'}/${referenceId}/questionnaire-draft`,
      { headers: getAuthHeaders(), cache: 'no-store' });
    return handleResponse(response);
  },
  saveQuestionnaireAnswers: async (referenceId, request, portal = false) => {
    const response = await fetch(`${config.api.url}/${portal ? 'client-portal/forms' : 'forms'}/${referenceId}/questionnaire-draft`, withJsonBody('PATCH', request));
    if (response.status === 409) {
      const error = new Error('This form changed in another session. Reload before saving again. Your unsaved answers are still on this screen.');
      error.code = 'questionnaireDraftConflict';
      throw error;
    }
    return handleResponse(response);
  },
  validateQuestionnaireDraft: async (templateReferenceId, versionReferenceId, questionnaireCatalogReferenceId, bindings, fields) => {
    const response = await fetch(
      `${config.api.url}/forms/templates/${templateReferenceId}/versions/${versionReferenceId}/questionnaire/validate-draft`,
      withJsonBody('POST', { questionnaireCatalogReferenceId, bindings, fields })
    );
    if (response.ok || response.status === 400) {
      const result = await response.json();
      if (Array.isArray(result.errors)) return result;
      throw new Error(result.error || 'The questionnaire could not be validated.');
    }
    return handleResponse(response);
  },
  previewFormTemplate: async (formTemplateReferenceId, request) => {
    const response = await fetch(
      `${config.api.url}/forms/templates/${formTemplateReferenceId}/preview`,
      withJsonBody('POST', request)
    );
    if (!response.ok) throw new Error('Unable to generate the sample PDF. Please try again.');
    return response.blob();
  },
  listFormTemplates: async (params = {}) => {
    const response = await fetch(
      `${config.api.url}/forms/templates${buildQueryString(params)}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  createFormTemplate: async (request) => {
    const response = await fetch(
      `${config.api.url}/forms/templates`,
      withJsonBody('POST', request)
    );
    return handleResponse(response);
  },

  getFormTemplate: async (formTemplateReferenceId) => {
    const response = await fetch(
      `${config.api.url}/forms/templates/${formTemplateReferenceId}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  updateFormTemplate: async (formTemplateReferenceId, request) => {
    const response = await fetch(
      `${config.api.url}/forms/templates/${formTemplateReferenceId}`,
      withJsonBody('PUT', request)
    );
    return handleResponse(response);
  },

  archiveFormTemplate: async (formTemplateReferenceId) => {
    const response = await fetch(
      `${config.api.url}/forms/templates/${formTemplateReferenceId}/archive`,
      {
        method: 'POST',
        headers: getAuthHeaders()
      }
    );
    return handleNoContentResponse(response);
  },

  listClientForms: async (clientReferenceId, params = {}) => {
    const response = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/forms${buildQueryString(params)}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  createFormInstance: async (clientReferenceId, request = {}) => {
    const response = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/forms`,
      withJsonBody('POST', request)
    );
    return handleResponse(response);
  },

  getFormInstance: async (formInstanceReferenceId) => {
    const response = await fetch(
      `${config.api.url}/forms/${formInstanceReferenceId}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  listFormMergeFields: async () => {
    const response = await fetch(
      `${config.api.url}/forms/merge-fields`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  saveFormDraft: async (formInstanceReferenceId, request = {}) => {
    const response = await fetch(
      `${config.api.url}/forms/${formInstanceReferenceId}/draft`,
      withJsonBody('PATCH', request)
    );
    return handleResponse(response);
  },

  refreshFormChartFields: async (formInstanceReferenceId, request = {}) => {
    const response = await fetch(
      `${config.api.url}/forms/${formInstanceReferenceId}/refresh-merge-fields`,
      withJsonBody('POST', request)
    );
    return handleResponse(response);
  },

  lockFormInstance: async (formInstanceReferenceId, request = {}) => {
    const response = await fetch(
      `${config.api.url}/forms/${formInstanceReferenceId}/lock`,
      withJsonBody('POST', request)
    );
    if (response.status === 409) throw Object.assign(new Error('This form changed. Reload before finalizing.'), { code: 'questionnaireDraftConflict' });
    return handleResponse(response);
  },

  voidFormInstance: async (formInstanceReferenceId, request = {}) => {
    const response = await fetch(
      `${config.api.url}/forms/${formInstanceReferenceId}/void`,
      withJsonBody('POST', buildVoidFormRequest(request))
    );
    return handleResponse(response);
  },

  publishFormInstance: async (formInstanceReferenceId) => {
    const response = await fetch(
      `${config.api.url}/forms/${formInstanceReferenceId}/publish`,
      {
        method: 'POST',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  listPortalForms: async () => {
    const response = await fetch(
      `${config.api.url}/client-portal/forms`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getPortalForm: async (formInstanceReferenceId) => {
    const response = await fetch(
      `${config.api.url}/client-portal/forms/${formInstanceReferenceId}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  createFormTemplateVersion: async (formTemplateReferenceId, request = {}) => {
    const response = await fetch(
      `${config.api.url}/forms/templates/${formTemplateReferenceId}/versions`,
      withJsonBody('POST', request)
    );
    return handleResponse(response);
  },

  uploadSourceDocument: async (formTemplateReferenceId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('velaToken');
    const response = await fetch(
      `${config.api.url}/forms/templates/${formTemplateReferenceId}/source-document`,
      {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      }
    );
    return handleResponse(response);
  },

  getSourceDocumentUrl: (formTemplateReferenceId, cacheKey, formTemplateVersionReferenceId) => {
    if (!formTemplateReferenceId) return null;
    const query = new URLSearchParams();
    if (cacheKey) query.append('v', cacheKey);
    if (formTemplateVersionReferenceId) {
      query.append('formTemplateVersionReferenceId', formTemplateVersionReferenceId);
    }
    const cacheQuery = query.toString() ? `?${query.toString()}` : '';
    return `${config.api.url}/forms/templates/${formTemplateReferenceId}/source-document${cacheQuery}`;
  },

  getProviderFinalPdfUrl: (formInstanceReferenceId, cacheKey) => {
    if (!formInstanceReferenceId) return null;
    const cacheQuery = cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : '';
    return `${config.api.url}/forms/${formInstanceReferenceId}/final-pdf${cacheQuery}`;
  },

  downloadProviderSourceDocument: async (formTemplateReferenceId, cacheKey, formTemplateVersionReferenceId) => {
    const url = formsApi.getSourceDocumentUrl(formTemplateReferenceId, cacheKey, formTemplateVersionReferenceId);
    return downloadPdf(url, `form-template-${formTemplateReferenceId}.pdf`);
  },

  downloadProviderFinalPdf: async (formInstanceReferenceId, cacheKey) => {
    const url = formsApi.getProviderFinalPdfUrl(formInstanceReferenceId, cacheKey);
    return downloadPdf(url, `form-${formInstanceReferenceId}-final.pdf`);
  },

  getPortalSourceDocumentUrl: (formInstanceReferenceId, cacheKey) => {
    if (!formInstanceReferenceId) return null;
    const cacheQuery = cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : '';
    return `${config.api.url}/client-portal/forms/${formInstanceReferenceId}/source-document${cacheQuery}`;
  },

  getPortalFinalPdfUrl: (formInstanceReferenceId, cacheKey) => {
    if (!formInstanceReferenceId) return null;
    const cacheQuery = cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : '';
    return `${config.api.url}/client-portal/forms/${formInstanceReferenceId}/final-pdf${cacheQuery}`;
  },

  downloadPortalSourceDocument: async (formInstanceReferenceId, cacheKey) => {
    const url = formsApi.getPortalSourceDocumentUrl(formInstanceReferenceId, cacheKey);
    return downloadPdf(url, `form-${formInstanceReferenceId}.pdf`);
  },

  downloadPortalFinalPdf: async (formInstanceReferenceId, cacheKey) => {
    const url = formsApi.getPortalFinalPdfUrl(formInstanceReferenceId, cacheKey);
    return downloadPdf(url, `form-${formInstanceReferenceId}-final.pdf`);
  }
};

export default formsApi;
